import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";

const FORMAT = "YYYY-MM-DD";

/**
 * Date-range filter synced to the URL query string. General-purpose: usable on
 * list pages, report pages, or anywhere a date range drives a query.
 *
 * @param {Object} [options]
 * @param {string} [options.startParam="startDate"] - URL/query param for the start date.
 * @param {string} [options.endParam="endDate"] - URL/query param for the end date.
 * @param {string[]} [options.resetParams=[]] - extra params to clear when the
 *   range changes (e.g. pass useServerPagination().resetParams on list pages to
 *   return to the first page). Must be referentially stable.
 * @returns {{
 *   startDate: import("dayjs").Dayjs | null,
 *   endDate: import("dayjs").Dayjs | null,
 *   dateRangeProps: Object, // spread into <CustomDateRangePicker />
 *   dateParams: Object,     // spread into request params
 * }}
 */
export const useDateRangeFilter = ({
  startParam = "startDate",
  endParam = "endDate",
  resetParams = [],
} = {}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const startValue = searchParams.get(startParam);
  const endValue = searchParams.get(endParam);

  const startDate = useMemo(
    () => (startValue ? dayjs(startValue) : null),
    [startValue],
  );
  const endDate = useMemo(
    () => (endValue ? dayjs(endValue) : null),
    [endValue],
  );

  const setDate = useCallback(
    (key, date) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        if (date) params.set(key, date.format(FORMAT));
        else params.delete(key);
        resetParams.forEach((param) => params.delete(param));
        return params;
      });
    },
    [setSearchParams, resetParams],
  );

  const dateRangeProps = {
    startDate,
    endDate,
    onStartDateChange: (date) => setDate(startParam, date),
    onEndDateChange: (date) => setDate(endParam, date),
  };

  // Already formatted YYYY-MM-DD in the URL; memoized for stable deps.
  const dateParams = useMemo(
    () => ({
      ...(startValue && { startDate: startValue }),
      ...(endValue && { endDate: endValue }),
    }),
    [startValue, endValue],
  );

  return { startDate, endDate, dateRangeProps, dateParams };
};
